import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ParallaxBackground from '../components/ParallaxBackground';
import { ChevronDown } from 'lucide-react';

const EFFECTIVE_DATE = 'January 1, 2026';

const SECTIONS = [
  {
    title: '1. About Petfolk',
    body: `Petfolk ("we", "us", or "our") is a professional pet grooming service operating across India through company-owned stores, franchise locations, and mobile grooming vans. By accessing petfolk.in or booking any service, you agree to these Terms & Conditions in full.`,
  },
  {
    title: '2. Eligibility & Account',
    body: `You must be at least 18 years old to create a Paws account and make bookings. By registering, you confirm that all information provided is accurate. You are responsible for maintaining the confidentiality of your account and all activity under it. Report any unauthorised access to support@petfolk.app immediately.`,
  },
  {
    title: '3. Booking & Cancellation Policy',
    body: `Bookings can be made online via petfolk.in or at any store. Appointments may be rescheduled up to 2 hours before the scheduled time at no charge. Cancellations made less than 2 hours before the appointment may forfeit any advance amount paid. No-shows without prior notice may result in a cancellation fee of up to ₹200.`,
  },
  {
    title: '4. Payments & Refunds',
    body: `Payments are processed securely via Razorpay. We accept UPI, debit/credit cards, net banking, and wallets. Cash payment is available at the store after service completion. Refunds for online payments are processed within 5–7 business days to the original payment method. Petfolk Credits earned through our loyalty programme are non-transferable and non-refundable for cash.`,
  },
  {
    title: '5. Paws Credits & Subscriptions',
    body: `Paws Credits are earned at a rate of 5% on every eligible completed booking and can be redeemed for up to 20% of a future order value. Credits expire 12 months from the date of earning. Subscription plans auto-renew unless cancelled at least 24 hours before the renewal date. Petfolk reserves the right to modify or discontinue any loyalty or subscription offering with 30 days' notice.`,
  },
  {
    title: '6. Pet Health & Liability',
    body: `You confirm that your pet is up to date on vaccinations (especially Rabies, Distemper, and Parvovirus) prior to any in-store visit. Petfolk is not liable for pre-existing health conditions, allergies, or stress-related reactions during grooming. In the event of a medical emergency during a session, Petfolk will seek veterinary assistance and you will be responsible for associated veterinary costs. We reserve the right to refuse service to aggressive animals or animals showing signs of illness.`,
  },
  {
    title: '7. Live Streaming',
    body: `Petfolk offers optional live-stream viewing of grooming sessions via the Paws app. Live streams are not recorded or stored by Petfolk. By enabling live stream, you consent to the real-time broadcast of your pet's session to your registered account only. Sharing of stream links outside your account is prohibited.`,
  },
  {
    title: '8. Franchise & Partner Stores',
    body: `Some Petfolk locations are operated by independent franchise partners under licence. While all franchisees are trained to Petfolk standards, Petfolk India Pvt. Ltd. cannot be held directly liable for acts or omissions of franchise-operated stores. Concerns regarding franchise stores should be reported to support@petfolk.app for resolution.`,
  },
  {
    title: '9. Intellectual Property',
    body: `All content on petfolk.in — including logos, images, text, and software — is the property of Petfolk India Pvt. Ltd. and protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written consent.`,
  },
  {
    title: '10. Governing Law & Disputes',
    body: `These Terms are governed by the laws of India. Any dispute arising from these Terms shall be subject to the exclusive jurisdiction of courts in Hyderabad, Telangana. We encourage resolution of disputes through our support team before pursuing legal remedies.`,
  },
  {
    title: '11. Changes to These Terms',
    body: `Petfolk reserves the right to update these Terms at any time. Changes take effect upon posting at petfolk.in/tnc. Continued use of our services after any update constitutes acceptance of the revised Terms. We will notify registered users of material changes via the Paws app or email.`,
  },
  {
    title: '12. Contact Us',
    body: `For questions about these Terms, contact us:\n\nEmail: support@petfolk.app\nPhone / WhatsApp: +91 9160153336\nHours: Monday – Sunday, 9 AM – 9 PM IST`,
  },
];

export default function TncPage() {
  const [open, setOpen] = useState(null);

  return (
    <div className="relative min-h-screen pt-20" style={{ background: 'var(--bg-primary)' }}>
      <ParallaxBackground />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 relative z-10">
        <div className="text-center mb-10">
          <h1 className="font-outfit text-3xl sm:text-4xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
            Terms &amp; <span className="gradient-text">Conditions</span>
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Effective date: {EFFECTIVE_DATE}</p>
        </div>

        <div className="space-y-3">
          {SECTIONS.map((s, i) => (
            <div key={i} className="petfolk-card overflow-hidden">
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full px-6 py-4 flex items-center justify-between text-left"
              >
                <span className="font-outfit font-semibold" style={{ color: 'var(--text-primary)' }}>{s.title}</span>
                <ChevronDown size={16} className={`transition-transform shrink-0 ml-4 ${open === i ? 'rotate-180' : ''}`} style={{ color: 'var(--text-secondary)' }} />
              </button>
              {open === i && (
                <div className="px-6 pb-5 text-sm leading-relaxed whitespace-pre-line" style={{ color: 'var(--text-secondary)' }}>
                  {s.body}
                </div>
              )}
            </div>
          ))}
        </div>

        <p className="mt-10 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
          Read our{' '}
          <Link to="/privacy" className="text-petfolk-orange hover:underline">Privacy Policy</Link>
          {' '}·{' '}
          <Link to="/support" className="text-petfolk-orange hover:underline">Contact Support</Link>
        </p>
      </div>
    </div>
  );
}
